<template>
  <PanelRow class="graphview">
    <div class="panel controls compact">
      <DeviceSelector v-model="chosen_ids" :devices="available_devices" />
      <IntervalSelector v-model="time_interval" @reload="reload" />
    </div>
    <div v-for="(graph, i) in graphs"
      class="panel compact graph-holder"
      :ref="'graph-' + i"
      :class="{fullscreen: fullscreen === i}"
    >
      <header>
        <h2>{{ graph.type.title | text }}</h2>
        <Icon
          :name="fullscreen ? 'fullscreen_exit' : 'fullscreen'"
          :title="$t('feelgrid:puredata.fullscreen')"
          @click.native="go_fullscreen(fullscreen ? false : i)"
        />
      </header>
      <Loading class="graph-loader" v-if="graph.datasets === 'loading'" />
      <Graph v-else-if="graph.datasets" :graph=graph :interval=graph_interval />
      <label v-else>{{ 'feelgrid:no_data' | trans }}</label>
    </div>
    <div class="panel user-help" v-if="!Object.keys(graphs).length">
      {{ 'feelgrid:puredata.select_device' | trans }}
    </div>
  </PanelRow>
</template>

<script>
  import base from './base'
  import {dateFromApi} from '@/providers/fields'
  import DeviceSelector from '../../assets/DeviceSelector'
  import Graph from '../../assets/Graph'
  import _ from 'lodash'

  //returns all possible variations of document fullscreen property (vendors, upper/lowercase Screen, ...)
  var fsproperty = function (base, el) {
    el = el || document;
    var r = false;
    var uncapitalize = s => s.charAt(0).toLowerCase() + s.slice(1);
    var capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);
    var b1 = capitalize(base.replace('screen', 'Screen'));//upper S
    var b2 = capitalize(base.replace('Screen', 'screen'));//lower s
    for (let vendor of ['', 'webkit', 'moz', 'ms']) {
      let v1 = uncapitalize(vendor + b1);
      let v2 = uncapitalize(vendor + b2);
      r = r || el[v1] || el[v2];
    }
    return r;
  };

  export default {
    name: 'GraphView',
    mixins: [base],
    components: {DeviceSelector, Graph},
    data() {return {
      chosen_ids: [],
      fullscreen: false,
      time_interval: '1h',
      collected_data: {},
      data_cancel: {},
      promises: {},
    }},
    computed: {
      fullscreen_listener () {
        return e => {
          var fullscreenElement = fsproperty('fullscreenElement');
          if (!fullscreenElement) {
            this.fullscreen = false;
          }
        };
      },
      chosen_devices () {
        return this.available_devices
          .filter(d => this.chosen_ids.includes(d.item.id))
        ;
      },
      //all shown data types of chosen devices
      graphs () {
        var t = {};
        for (let device of this.chosen_devices) {
          for (let type of device.item.hardware_types.data_types) {
            let tid = type.id;
            if (!t.hasOwnProperty(tid)) {
              t[tid] = {
                type,
                data_type_id: tid,
                devices: {},
                datasets: Object.keys(this.promises).length ? 'loading' : false,
                loading: [],
              };
            }
            let device_id = device.item.id;
            t[tid].devices[device_id] = device;
            if (this.collected_data[device_id] && this.collected_data[device_id][tid] && this.collected_data[device_id][tid].length) {
              if (typeof t[tid].datasets !== 'object') {
                t[tid].datasets = {};
              }
              var d = [].concat(this.collected_data[device_id][tid]);
              _.reverse(d);
              t[tid].datasets[device_id] = _.sortBy(d, o => o.t);
              //if data is still incoming, add a dummy point at the start
              if (this.promises[device_id]) {
                t[tid].loading.push(device_id);
                var ds = t[tid].datasets[device_id];
                t[tid].datasets[device_id].unshift({
                  t: this.date_limits[0],
                  //y: ds[0].y,
                  y: null,
                });
                t[tid].datasets[device_id].push({
                  t: this.date_limits[1],
                  //y: ds[ds.length - 1].y,
                  y: null,
                });
              }
            }
          }
        }
        return t;
      },
    },
    methods: {
      go_fullscreen (i, evt) {
        if (i) {
          var elem = this.$refs['graph-' + i][0];
          var requestFullscreen = fsproperty('requestFullscreen', elem);
          if (requestFullscreen) {
            requestFullscreen.call(elem);
            this.fullscreen = i;
          }
        } else {
          var exitFullscreen = fsproperty('exitFullscreen') || fsproperty('cancelFullscreen');
          if (exitFullscreen) {
            exitFullscreen.call(document);
            this.fullscreen = i;
          }
        }
      },
      //cancels a pending request if it exists
      cancel (id) {
        if (typeof this.data_cancel[id] === 'object') {
          if (this.data_cancel[id].function) {
            this.data_cancel[id].function();
          }
          this.$delete(this.data_cancel, id);
        }
        if (this.promises.hasOwnProperty(id)) {
          this.$delete(this.collected_data, id);
          this.$delete(this.promises, id);
        }
      },
      //cancels all requests and clears all data
      cancel_all () {
        for (let id in this.data_cancel) {
          this.cancel(id);
        }
        this.promises = {};
        this.collected_data = {};
        this.data_cancel = {};
      },

      //returns a function that fills device data
      generate_fill_function (device_id, data_types) {
        return (records, index, all) => {
          if (!this.collected_data.hasOwnProperty(device_id)) {
            this.$set(this.collected_data, device_id, {});
          }
          //transform data to this shape:
          // {
          //  DATA_TYPE_ID_1: [
          //    {t: TIME_1, y: VALUE_11},
          //    {t: TIME_2, y: VALUE_21},
          //    {t: TIME_3, y: VALUE_31},
          //    ...
          //  ],
          //  DATA_TYPE_ID_2: [
          //    {t: TIME_1, y: VALUE_12},
          //    {t: TIME_2, y: VALUE_22},
          //    {t: TIME_3, y: VALUE_32},
          //    ...
          //  ],
          //
          //for min-average-max interval
          //
          //  DATA_TYPE_ID_1: [
          //    {t: TIME_1, min: MIN_11, avg: AVERAGE_11, max: MAX_11},
          //    {t: TIME_2, min: MIN_21, avg: AVERAGE_21, max: MAX_21},
          //    ...
          //  ],
          //  ...
          //}
          var split_data = {};
          var recordtype = 'point';
          if (records.timestamp) {
            recordtype = 'interval';
          }
          for (let item of records.data) {
            let x;
            if (recordtype === 'point') {
              x = dateFromApi(item.created_at);
            } else {
              x = dateFromApi(item.timestamp);
            }
            for (let type of data_types) {
              if (!item[type]) {
                continue;
              }
              let type_in_item = item[type];
              if (!Array.isArray(type_in_item)) {
                type_in_item = [type_in_item];
              }
              for (let s of type_in_item) {
                if (!split_data.hasOwnProperty(s.data_types_id)) {
                  split_data[s.data_types_id] = [];
                }
                let point;
                if (recordtype === 'point') {
                  point = {t: x, y: Number(s.value)};
                } else {
                  point = {
                    t: x,
                    min: Number(s.value_min),
                    avg: Number(s.value_avg),
                    max: Number(s.value_max),
                  };
                }
                split_data[s.data_types_id].push(point);
              }
            }
          }
          var combined = {};
          var allkeys = _.uniq(Object.keys(this.collected_data[device_id]).concat(Object.keys(split_data)));
          for (let type_id of allkeys) {
            let union;
            let exists = this.collected_data[device_id].hasOwnProperty(type_id);
            let isnew = split_data.hasOwnProperty(type_id);
            if (exists && isnew) {
              union = this.collected_data[device_id][type_id].concat(split_data[type_id]);
            } else if (isnew) {
              union = split_data[type_id];
            } else if (exists) {
              union = this.collected_data[device_id][type_id];
            }
            combined[type_id] = union;
          }
          this.$set(this.collected_data, device_id, combined);
        };
      },
      //returns a pending promise, existing data or starts loading new data
      load_device (id) {
        if (this.promises[id]) {
          return this.promises[id];
        } else if (this.collected_data[id]) {
          return Promise.resolve(this.collected_data[id]);
        }
        var device = this.available_devices
          .filter(d => d.item.id === id)[0]
        ;
        var data_types = device.item.hardware_types.data_types
          .map(type => _.snakeCase(type.name))
        ;
        this.$set(this.data_cancel, id, {});
        var promise;
        if (this.resolution === 0) {
          promise = this.api.read(
            'devices_data' +
              '?filter=devices_id::eq::' + id +
              ',' + this.date_range_filter +
              '&sort=id::desc' +
              '&range=all' +
              '&include=' + data_types.join(',')
            ,
            this.generate_fill_function(id, data_types),
            this.data_cancel[id],
            500
          );
        } else {
          promise = this.api.get(
            'devices/' + id + '/data?' + this.resolution_filter + '&page=1',
            this.data_cancel[id],
          )
          .then(this.generate_fill_function(id, data_types));
        }
        //when all data is loaded, remove promise from pending promises list
        promise = promise.then(() => {
          this.$nextTick(() => {
            this.$delete(this.promises, id);
            this.$delete(this.data_cancel, id);
          });
        });
        this.$set(this.promises, id, promise);
        this.$set(this.collected_data, id, {});
        return promise;
      },
      //cancels all pending unneeded requests, starts new requests if necessary
      start_loading () {
        for (let id in this.collected_data) {
          if (!this.chosen_ids.includes(id)) {
            this.cancel(id);
          }
        }
        for (let id of this.chosen_ids) {
          this.load_device(id);
        }
      },
    },
    watch: {
      date_range_filter () {
        this.collected_data = {};
        this.cancel_all();
        this.start_loading();
      },
      chosen_ids: {
        deep: true,
        handler (v) {
          this.start_loading();
        },
      },
      'available_devices.length' (l) {
        if (l) {
          this.chosen_ids = this.available_devices.map(d => d.id);
        }
      },
    },
    mounted () {
      this.start_loading();
      ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'msscreenchange']
        .forEach(e => {
          document.addEventListener(e, this.fullscreen_listener);
        })
      ;
      if (this.available_devices.length) {
        this.chosen_ids = this.available_devices.map(d => d.id);
      }
    },
    beforeDestroy () {
      this.cancel_all();
      ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'msscreenchange']
        .forEach(e => {
          document.removeEventListener(e, this.fullscreen_listener);
        })
      ;
    },
  }
</script>

<style lang="scss">
  @import '../../scss/variables';

  .feelgrid-puredata .graphview {
    .graph-holder {
      > header > svg {
        color: $green;
        cursor: pointer;
      }
      .graph-loader, canvas {
        height: 300px !important;
      }
      canvas {
        width: 100% !important;
      }
      &.fullscreen canvas {
        height: calc(100vh - 100px) !important;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/apps/feelgrid/modules/pureData/GraphView.vue